import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import VoiceMessagePlayer from './VoiceMessagePlayer';
import LanguageContext from '../helper/LanguageContext';
import { cleanTranscript, isInvalidTranscript } from '../utils/conversationUtils';
import { findMatchRanges, parseSearchQuery } from './chat/chatSearchIndex';
import { markSearchTerms } from './chat/ChatMessageMarkdown';
import './VoiceMessageWithTranscript.css';

const REMARK_PLUGINS = [remarkGfm, remarkBreaks];

/**
 * Voice message with its AI transcript (mobile ProVoiceRow + ProTranscriptBox): the
 * player, then the always visible transcript box — `graphic_eq` "KI-Transkript", the
 * text clamped to two lines with "Mehr anzeigen" / "Weniger anzeigen", "Transkription
 * läuft…" while pending and "Kein Transkript verfügbar." when there is none. The
 * translation toggle (original / translation) stays in the box header.
 * Same props as before; senderAvatar is no longer drawn (the mobile row has none).
 *
 * `highlight` (in-chat search; a query or { terms, wordStartOnly }, as ChatMessageMarkdown): a
 * transcript that matches opens in full with the matches marked, on the version (translation or
 * original) that contains them.
 */
const VoiceMessageWithTranscript = ({ src, audioType, transcribedAudio, transcriptionPending, onRequestTranscript, translationPending, originalTranscribedAudio, highlight = "" }) => {
  const { strings, isGerm } = React.useContext(LanguageContext);
  const [showFull, setShowFull] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  // Once the reader picks a version, search no longer switches it.
  const [versionPicked, setVersionPicked] = useState(false);
  const [clamped, setClamped] = useState(false);
  const textRef = useRef(null);
  const t = (key, de, en) => (strings && strings[key]) || (isGerm ? de : en);

  const terms = highlight && typeof highlight === 'object'
    ? (Array.isArray(highlight.terms) ? highlight.terms : [])
    : parseSearchQuery(highlight);
  const wordStartOnly = !!(highlight && typeof highlight === 'object' && highlight.wordStartOnly);
  const termKey = terms.join(' ');
  const matches = (text) => !!termKey && findMatchRanges(cleanTranscript(text) || '', terms, wordStartOnly).length > 0;

  const hasTranslation = !translationPending && originalTranscribedAudio && originalTranscribedAudio !== transcribedAudio;
  // A search hit only in the original shows the original (until the search ends).
  const matchInShown = matches(showOriginal && hasTranslation ? originalTranscribedAudio : transcribedAudio);
  const matchInOther = hasTranslation && !versionPicked && !matchInShown && matches(showOriginal ? transcribedAudio : originalTranscribedAudio);
  const showingOriginal = hasTranslation && (matchInOther ? !showOriginal : showOriginal);
  const searchMatch = matchInShown || matchInOther;
  const activeTranscript = showingOriginal ? originalTranscribedAudio : transcribedAudio;
  const expanded = showFull || searchMatch;
  const rehypePlugins = React.useMemo(
    () => (termKey ? [markSearchTerms(termKey.split(' '), wordStartOnly)] : []),
    [termKey, wordStartOnly]
  );

  useEffect(() => {
    if (!transcribedAudio && !transcriptionPending && onRequestTranscript) {
      onRequestTranscript();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanedAudio = cleanTranscript(activeTranscript);
  const transcriptInvalid = isInvalidTranscript(cleanedAudio);
  const hasText = !!cleanedAudio && !transcriptInvalid;
  // No text yet: in progress while it is pending or was just requested.
  const transcribing = !hasText && !transcriptInvalid && (transcriptionPending || !!onRequestTranscript);
  const busy = translationPending || transcribing;

  // "Mehr anzeigen" only when two lines are not enough.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setClamped(el.scrollHeight - el.clientHeight > 2);
  }, [cleanedAudio, expanded, busy]);

  let body;
  if (busy) {
    body = (
      <div className="kjc-transcript__state" aria-busy="true">
        {translationPending ? t('translating', 'Übersetzen…', 'Translating…') : t('chat_voice_transcribing', 'Transkription läuft…', 'Transcription in progress…')}
      </div>
    );
  } else if (!hasText) {
    body = <div className="kjc-transcript__state">{t('no_transcript_available', 'Kein Transkript verfügbar.', 'No transcript available.')}</div>;
  } else {
    body = (
      <>
        <div ref={textRef} className={`kjc-transcript__text${expanded ? '' : ' is-clamped'}`}>
          <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={rehypePlugins}>{cleanedAudio}</Markdown>
        </div>
        {(clamped || showFull) && !searchMatch ? (
          <div
            className="kjc-transcript__more"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowFull((v) => !v);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowFull((v) => !v);
              }
            }}
          >
            {showFull ? t('show_less', 'Weniger anzeigen', 'Show less') : t('show_more', 'Mehr anzeigen', 'Show more')}
          </div>
        ) : null}
      </>
    );
  }

  // Divs, not spans: chat bubbles force their own font size and colour onto spans.
  return (
    <div className="voice-transcript-wrapper kjc-voice-msg">
      <VoiceMessagePlayer src={src} audioType={audioType} wide />
      <div className="kjc-transcript">
        <div className="kjc-transcript__head">
          <div className="material-symbols-outlined kjc-transcript__icon">graphic_eq</div>
          <div className="kjc-transcript__label">{t('chat_voice_ai_transcript', 'KI-Transkript', 'AI transcript')}</div>
          {hasTranslation ? (
            <div
              className="kjc-transcript__toggle"
              role="button"
              tabIndex={0}
              title={showingOriginal ? (strings.click_to_show_translation || 'Click to show translation') : (strings.click_to_show_original || 'Click to show original')}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setVersionPicked(true);
                setShowOriginal(!showingOriginal);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setVersionPicked(true);
                setShowOriginal(!showingOriginal);
                }
              }}
            >
              {showingOriginal ? t('show_translation', 'Übersetzung anzeigen', 'Show translation') : t('show_original', 'Original anzeigen', 'Show original')}
            </div>
          ) : null}
        </div>
        {body}
      </div>
    </div>
  );
};

export default React.memo(VoiceMessageWithTranscript);
